import { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../../context/AppContext';
import { getExpenses, createExpense, updateExpense, deleteExpense, getRevenue } from '../../services/adminService';

const CATEGORIES = [
  { value: '', label: 'All categories' },
  { value: 'rent', label: 'Rent' },
  { value: 'ads', label: 'Ads' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'studio', label: 'Studio' },
  { value: 'other', label: 'Other' },
];

const MONTHS = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

function formatExpenseDate(date) {
  if (!date) return '—';
  const d = new Date(date);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function toDateInputValue(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().slice(0, 10);
}

export default function AdminExpenses({ token }) {
  const { setToaster } = useAppContext();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rentLoading, setRentLoading] = useState(false);
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(String(currentMonth));
  const [view, setView] = useState('month'); // 'month' | 'year'
  const [rentExpensesILS, setRentExpensesILS] = useState(0);
  const requestIdRef = useRef(0);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState({
    amount: '',
    currency: 'ILS',
    category: 'other',
    date: toDateInputValue(new Date()),
    notes: '',
  });
  const monthNames = MONTHS.slice(1);

  const shiftViewMonth = (delta) => {
    const currentMonthNum = Number(month) || currentMonth;
    let nextMonth = currentMonthNum + delta;
    let nextYear = Number(year);

    while (nextMonth < 1) {
      nextMonth += 12;
      nextYear -= 1;
    }
    while (nextMonth > 12) {
      nextMonth -= 12;
      nextYear += 1;
    }

    setYear(nextYear);
    setMonth(String(nextMonth));
  };

  const fetchExpenses = async (requestId = null) => {
    if (!token) return;
    setLoading(true);
    setExpenses([]);
    try {
      const params = { year };
      if (month) params.month = month;
      const data = await getExpenses(token, params);
      if (requestId !== null && requestId !== requestIdRef.current) return;
      setExpenses(data.expenses || []);
    } catch (error) {
      if (requestId !== null && requestId !== requestIdRef.current) return;
      console.error('Error fetching expenses:', error);
      setToaster({ message: 'Failed to load expenses', type: 'error', show: true });
    } finally {
      if (requestId !== null && requestId !== requestIdRef.current) return;
      setLoading(false);
    }
  };

  const fetchRent = async (requestId = null) => {
    if (!token || !year) return;
    setRentLoading(true);
    setRentExpensesILS(0);
    try {
      const data = await getRevenue(token, year);
      const ils = data?.byCurrency?.ILS;
      const upcomingYear = ils?.upcomingRentExpensesThisYear ?? data?.upcomingRentExpensesThisYear ?? 0;
      const upcomingMonth = ils?.upcomingRentExpensesThisMonth ?? data?.upcomingRentExpensesThisMonth ?? 0;
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      const monthNum = month ? Number(month) : null;
      let pastRent = 0;
      if (monthNum && data?.byCurrency?.ILS?.monthlyBreakdown) {
        const m = data.byCurrency.ILS.monthlyBreakdown.find((row) => row.month === monthNum);
        pastRent = m?.rentExpenses ?? 0;
      } else {
        pastRent = data?.rentExpenses ?? ils?.totalRentExpenses ?? 0;
      }
      // Monthly view: past rent for this month + upcoming classes until end of month. Yearly: past + all upcoming in year.
      const isCurrentMonth = Number(year) === currentYear && monthNum === currentMonth;
      const upcoming = monthNum ? (isCurrentMonth ? upcomingMonth : 0) : upcomingYear;
      if (requestId !== null && requestId !== requestIdRef.current) return;
      setRentExpensesILS(pastRent + upcoming);
    } catch (error) {
      if (requestId !== null && requestId !== requestIdRef.current) return;
      console.warn('Error fetching rent expenses:', error);
      setRentExpensesILS(0);
    } finally {
      if (requestId !== null && requestId !== requestIdRef.current) return;
      setRentLoading(false);
    }
  };

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    fetchExpenses(requestId);
    fetchRent(requestId);
  }, [token, year, month]);

  const openAdd = () => {
    setEditingId(null);
    setForm({
      amount: '',
      currency: 'ILS',
      category: 'other',
      date: toDateInputValue(new Date()),
      notes: '',
    });
    setShowForm(true);
  };

  const openEdit = (exp) => {
    setEditingId(exp._id);
    setForm({
      amount: String(exp.amount),
      currency: exp.currency || 'ILS',
      category: exp.category || 'other',
      date: toDateInputValue(exp.date),
      notes: exp.notes || '',
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (Number.isNaN(amount) || amount < 0) {
      setToaster({ message: 'Enter a valid amount', type: 'error', show: true });
      return;
    }
    const payload = {
      amount,
      currency: form.currency,
      category: form.category,
      date: form.date,
      notes: form.notes.trim(),
    };
    try {
      if (editingId) {
        await updateExpense(token, editingId, payload);
        setToaster({ message: 'Expense updated', type: 'success', show: true });
      } else {
        await createExpense(token, payload);
        setToaster({ message: 'Expense added', type: 'success', show: true });
      }
      closeForm();
      fetchExpenses();
    } catch (error) {
      setToaster({ message: error.message || 'Failed to save expense', type: 'error', show: true });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    setDeletingId(id);
    try {
      await deleteExpense(token, id);
      setToaster({ message: 'Expense deleted', type: 'success', show: true });
      fetchExpenses();
    } catch (error) {
      setToaster({ message: error.message || 'Failed to delete', type: 'error', show: true });
    } finally {
      setDeletingId(null);
    }
  };

  const symbol = (c) => (c === 'RUB' ? '₽' : '₪');
  
  // Filter out class costs and rent-category expenses for totals calculation
  // (both are already included in rentExpensesILS — see server's revenue.js)
  const manualExpenses = expenses.filter(e => !e.isClassCost && e.category !== 'rent');
  
  const totalByCurrency = manualExpenses.reduce((acc, e) => {
    const c = e.currency || 'ILS';
    acc[c] = (acc[c] || 0) + (e.amount || 0);
    return acc;
  }, {});
  const otherILS = totalByCurrency.ILS || 0;
  const totalILS = otherILS + (rentExpensesILS || 0);

  // Breakdown by category: count + totals per currency (rent first, then others)
  // For category breakdown, include all expenses (manual + class costs)
  const categoryOrder = ['rent', 'ads', 'utilities', 'supplies', 'studio', 'other'];
  const byCategory = expenses.reduce((acc, e) => {
    const cat = e.category || 'other';
    if (!acc[cat]) acc[cat] = { count: 0, ILS: 0, RUB: 0 };
    // Only count manual expenses in the count, but include all in amounts
    if (!e.isClassCost) acc[cat].count += 1;
    const c = e.currency === 'RUB' ? 'RUB' : 'ILS';
    acc[cat][c] += e.amount || 0;
    return acc;
  }, {});
  const sortedCategories = categoryOrder.filter((cat) => byCategory[cat]);

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mb-4">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
          <button
            type="button"
            onClick={() => {
              setView('month');
              if (!month) {
                setYear(currentYear);
                setMonth(String(currentMonth));
              }
            }}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition ${
              view === 'month' ? 'bg-brown text-white' : 'text-gray-600 hover:text-brown'
            }`}
          >
            This month
          </button>
          <button
            type="button"
            onClick={() => {
              setView('year');
              setYear(currentYear);
              setMonth('');
            }}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition ${
              view === 'year' ? 'bg-brown text-white' : 'text-gray-600 hover:text-brown'
            }`}
          >
            Year
          </button>
        </div>
        {view === 'month' && (
          <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white">
            <button
              type="button"
              onClick={() => shiftViewMonth(-1)}
              className="px-2.5 py-1.5 text-sm font-semibold text-gray-600 hover:text-brown hover:bg-gray-50 rounded-l-lg transition"
              aria-label="Previous month"
            >
              ‹
            </button>
            <span className="px-2 py-1.5 text-xs font-semibold text-gray-800 tabular-nums min-w-[6.5rem] text-center border-x border-gray-200">
              {monthNames[(Number(month) || currentMonth) - 1]} {year}
            </span>
            <button
              type="button"
              onClick={() => shiftViewMonth(1)}
              className="px-2.5 py-1.5 text-sm font-semibold text-gray-600 hover:text-brown hover:bg-gray-50 rounded-r-lg transition"
              aria-label="Next month"
            >
              ›
            </button>
          </div>
        )}
        {view === 'year' && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 shrink-0">Month:</label>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            >
              <option value="">All</option>
              {MONTHS.slice(1).map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
        )}
        <button
          type="button"
          onClick={openAdd}
          className="px-3 py-1.5 bg-brown text-white rounded text-sm hover:opacity-90 touch-manipulation"
        >
          Add expense
        </button>
        <button
          type="button"
          onClick={fetchExpenses}
          disabled={loading}
          className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 touch-manipulation"
        >
          Refresh
        </button>
      </div>

      {!loading && !rentLoading && (otherILS > 0 || rentExpensesILS > 0 || totalByCurrency.RUB > 0) && (
        <div className="flex flex-wrap gap-4 text-sm">
          {totalILS > 0 && (
            <span className="text-gray-900">Total (ILS): <strong>₪{totalILS.toLocaleString()}</strong></span>
          )}
          {rentExpensesILS > 0 && (
            <span className="text-gray-700">Rent (classes): <strong>₪{rentExpensesILS.toLocaleString()}</strong></span>
          )}
          {otherILS > 0 && (
            <span className="text-gray-700">Other (ILS): <strong>₪{otherILS.toLocaleString()}</strong></span>
          )}
          {totalByCurrency.RUB > 0 && (
            <span className="text-gray-700">Total (RUB): <strong>₽{totalByCurrency.RUB.toLocaleString()}</strong></span>
          )}
        </div>
      )}

      {!loading && !rentLoading && sortedCategories.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Breakdown by category</h4>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {sortedCategories.map((cat) => {
              const row = byCategory[cat];
              const textParts = [];
              if (row.count !== 0) {
                textParts.push(`${row.count} ${row.count === 1 ? 'expense' : 'expenses'}`);
              }
              if (row.ILS > 0) textParts.push(`₪${row.ILS.toLocaleString()}`);
              if (row.RUB > 0) textParts.push(`₽${row.RUB.toLocaleString()}`);
              return (
                <div key={cat} className="flex items-baseline gap-2">
                  <span className="capitalize font-medium text-gray-800">{cat}</span>
                  <span className="text-gray-600">— {textParts.join(', ')}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-center py-6 text-gray-500 text-sm">Loading…</p>
      ) : (
        <>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left text-sm border border-gray-200 rounded overflow-hidden min-w-[560px]">
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr>
                  <th className="py-2 px-3 font-semibold">Date</th>
                  <th className="py-2 px-3 font-semibold">Category</th>
                  <th className="py-2 px-3 font-semibold text-right">Amount</th>
                  <th className="py-2 px-3 font-semibold">Notes</th>
                  <th className="py-2 px-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                      No expenses. Add rent, ads, utilities, etc.
                    </td>
                  </tr>
                ) : (
                  expenses.map((exp) => (
                    <tr key={exp._id} className={`border-b border-gray-100 ${exp.isClassCost ? 'bg-blue-50/30' : 'hover:bg-gray-50'}`}>
                      <td className="py-2 px-3 text-left">{formatExpenseDate(exp.date)}</td>
                      <td className="py-2 px-3 text-left capitalize">{exp.category || 'other'}</td>
                      <td className="py-2 px-3 text-right font-medium">
                        {symbol(exp.currency)}{(exp.amount || 0).toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-left text-gray-600 max-w-[200px] whitespace-normal break-words align-top">
                        {exp.notes || '—'}
                      </td>
                      <td className="py-2 px-3">
                        {exp.isClassCost ? (
                          <span className="text-xs text-gray-500 italic">Auto (from class)</span>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => openEdit(exp)}
                              className="text-brown hover:underline text-sm mr-2"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(exp._id)}
                              disabled={deletingId === exp._id}
                              className="text-red-600 hover:underline text-sm disabled:opacity-50"
                            >
                              {deletingId === exp._id ? '…' : 'Delete'}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="md:hidden divide-y divide-gray-100">
            {expenses.length === 0 ? (
              <p className="py-6 text-center text-gray-500 text-sm">No expenses. Add rent, ads, utilities, etc.</p>
            ) : (
              expenses.map((exp) => (
                <div key={exp._id} className={`py-3 ${exp.isClassCost ? 'bg-blue-50/30' : ''}`}>
                  <div className="flex justify-between items-start">
                    <div className="text-left">
                      <p className="font-medium capitalize">{exp.category || 'other'}</p>
                      <p className="text-sm text-gray-600">{formatExpenseDate(exp.date)}</p>
                      {exp.notes && (
                        <p className="text-sm text-gray-500 mt-0.5 whitespace-normal break-words">
                          {exp.notes}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{symbol(exp.currency)}{(exp.amount || 0).toLocaleString()}</p>
                      {exp.isClassCost ? (
                        <p className="text-xs text-gray-500 italic mt-1">Auto (from class)</p>
                      ) : (
                        <div className="mt-1 flex gap-2 justify-end">
                          <button type="button" onClick={() => openEdit(exp)} className="text-brown text-sm">Edit</button>
                          <button
                            type="button"
                            onClick={() => handleDelete(exp._id)}
                            disabled={deletingId === exp._id}
                            className="text-red-600 text-sm disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={closeForm}>
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit expense' : 'Add expense'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  value={form.currency}
                  onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="ILS">ILS (₪)</option>
                  <option value="RUB">RUB (₽)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  {CATEGORIES.filter((c) => c.value).map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="e.g. March rent, Facebook ads"
                  rows={3}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-y min-h-[80px]"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-brown text-white rounded text-sm hover:opacity-90"
                >
                  {editingId ? 'Update' : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
